import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  computed, effect, inject, input, OnDestroy, OnInit, signal, untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { KartWebSocketService } from '../kart-okuyucu/services/kart-websocket.service';
import { AvatarService } from '@/src/app/servisler/avatar.service';

export interface IFirmadaKisi {
  kartUid: string;
  isim: string;
  resimId: string;
  resimUrl: string;
  okuyucuKimlik: string;
  okuyucuIsmi: string;
  girisSaati: string;   // 'HH:MM'
}

/** Bir kartın gün içindeki okutmalarını "dokunuş" (occasion) gruplarına ayırmak için
 *  kullanılan eşik (ms) — birbirine bu süreden yakın okutmalar (koyuldu+cekildi ikilisi
 *  dahil) TEK bir dokunuş sayılır.
 *
 *  ÖNEMLİ: Bu artık SABİT bir değer DEĞİL — her kart kendi kişiye özel eşiğini
 *  kart-sahibi bileşen kaydının `ek.grupEsikSaniye` alanından (DB) okur, bkz. _esikGetir().
 *  Alan tanımlı değilse (eski kayıt / hiç ayarlanmamış) bu varsayılan kullanılır — kart-sahibi
 *  bileşenindeki varsayılan gruplama eşiğiyle (grupEsikSaniye=5) birebir aynı. */
const DOKUNUS_ESIK_VARSAYILAN_MS = 5000;

@Component({
  selector: 'firmada-panel-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './firmada-panel-kart.component.html',
  styleUrl: './firmada-panel-kart.component.less',
})
export class FirmadaPanelKartComponent implements OnInit, OnDestroy {
  parametreler = input<any>();

  firmadakiler = signal<IFirmadaKisi[]>([]);
  yukleniyor   = signal(false);

  baslik           = computed(() => this.parametreler()?.ek?.baslik || 'Firmada Olanlar');
  secilenOkuyucular = computed<string[]>(() => this.parametreler()?.ek?.secilenOkuyucular ?? []);

  private _destroy$ = new Subject<void>();
  private wsService = inject(KartWebSocketService);
  private avatar    = inject(AvatarService);
  private cd        = inject(ChangeDetectorRef);

  /** Açılış yüklemesinin en az bir kez yapıldığını işaretler (çift/yinelenen yükleme yok). */
  private _acilisYuklendi = false;

  /** Canlı dokunuş takibi — kart(küçük harf) -> son dokunuş durumu.
   *  DB yazımı asenkron (sunucuda goroutine) olduğu için, bir WS olayı geldiğinde DB henüz
   *  güncellenmemiş olabiliyor; bu haritayla DB'ye tekrar dönmeye gerek kalmadan her dokunuş
   *  anında "içeride/içeride değil" geçişini yapabiliyoruz. Başlangıç değeri açılış yüklemesinde
   *  DB'den tohumlanır (firmada = dokunuş sayısı tek). */
  private _dokunusVeri = new Map<string, { firmada: boolean; sonDokunusZaman: number }>();

  /** Kart başına kişiye özel dokunuş gruplama eşiği (ms) önbelleği — kart-sahibi'nin
   *  DB'deki `ek.grupEsikSaniye` alanını her okutmada tekrar sorgulamamak için. kart-sahibi
   *  tarafında ayar değişince (hareketDegisti$ sinyali) temizlenir, bkz. ngOnInit. */
  private _esikCache = new Map<string, number>();

  constructor() {
    // `parametreler` ebeveyn (ikart-kutusu) tarafından kart DB'den geldikten SONRA
    // asenkron doldurulabildiği için, ngOnInit çalıştığında `secilenOkuyucular()` henüz
    // boş olabilir ve açılış yüklemesi (`_acilisYukle`) boş küçe ile erken döner → panel
    // boş kalır. Bu effect, `ek.secilenOkuyucular` boştan doluya geçtiği anda (daha önce
    // yüklenmemişse) açılış yüklemesini tetikler. untracked: effect yalnızca parametre
    // değişimine tepki versin, içindeki okuma/yazmalar bağımlılığa karışmasın.
    effect(() => {
      const secili = this.secilenOkuyucular();
      if (secili.length && !this._acilisYuklendi) {
        this._acilisYuklendi = true;
        untracked(() => this._acilisYukle());
      }
    });
  }

  ngOnInit() {
    // Açılışta mevcut durumu DB'den çek
    this._acilisYukle();

    // Kart-sahibi tarafında yapılan değişiklikler (manuel giriş/çıkış ekleme, düzenleme,
    // grup düzenleme/silme) doğrudan `pdks_kart_hareketler`'e yazıldığı için WS olayı
    // ÜRETMEZ. Kart-sahibi bu değişikliklerden sonra `hareketDegisti$` sinyali yollar —
    // onu dinleyip durumu ANINDA yeniden hesaplıyoruz (periyodik polling YOK).
    // debounce: tek bir düzenlemede birkaç kayıt değişebildiği için ardışık sinyalleri
    // tek yenilemeye indirger.
    this.wsService.hareketDegisti$
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe(() => {
        // Kart-sahibi tarafında tolerans/gruplama ayarı değişmiş olabilir —
        // önbelleklenmiş eşikleri at ki bir sonraki hesaplama güncel DB değerini okusun.
        this._esikCache.clear();
        this._acilisYukle(true);
      });

    // WS canlı olayları — seçili okuyuculardan gelen HER olayda (koyuldu VEYA cekildi)
    // ilgili kartın bugünkü dokunuş sayısını yeniden hesaplayıp listeyi güncelle.
    // NOT: Kart okuyucuda sürekli kalmıyor — çalışan kartı okutup hemen cebine koyuyor
    // (koyuldu+cekildi neredeyse eş zamanlı gelir). Bu yüzden "son olay koyuldu mu"
    // diye bakmak YANLIŞ: aynı gün içinde giriş/izin/dönüş döngüsünde her okutma bir
    // öncekinin tam tersini ifade eder. Doğru kural kart-sahibi'ndeki ile aynı:
    // bugünün dokunuşları sırayla giriş/çıkış/giriş/çıkış... sayılır, TEK sayıda
    // dokunuş = hâlâ içeride.
    this.wsService.kartOlayiHam$.pipe(takeUntil(this._destroy$)).subscribe(async olay => {
      const secili = this.secilenOkuyucular();
      if (!secili.length || !olay.kartKimlik) return;
      const cihazEslesmesi = secili.some(s => String(s).toLowerCase() === String(olay.cihazKimlik).toLowerCase());
      if (!cihazEslesmesi) return;
      await this._kartiGuncelle(olay.kartKimlik, olay.cihazKimlik, olay.zaman?.getTime?.() ?? Date.now());
    });

    if (!this.wsService.bagli) this.wsService.baglan();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Açılışta: seçili okuyucularda bugün en az bir kez okutulmuş kartları bul,
   *  her biri için güncel giriş/çıkış durumunu hesapla, firmada olanları listele.
   *  @param sessiz true ise (canlı tazeleme) `yukleniyor` spinner'ı GÖSTERİLMEZ ve
   *  liste fark-temelli güncellenir — içerik değişmemişse DOM'a hiç dokunulmaz, böylece
   *  kapsayıcı boyutları titremez. */
  private async _acilisYukle(sessiz = false) {
    const secili = this.secilenOkuyucular();
    if (!secili.length) return;
    this._acilisYuklendi = true;
    if (!sessiz) {
      this.yukleniyor.set(true);
      this.cd.markForCheck();
    }
    try {
      // Seçili okuyucularda BUGÜN okutulmuş benzersiz kartları bul — kart-sahibi.firmadaMi
      // ile aynı: durum bugünün okutmalarına göre belirlenir.
      // ÖNEMLİ: Kart-sahibi'nde eklenen MANUEL kayıtların `cihaz_kimlik` alanı YOKTUR
      // (bkz. kart-sahibi manuelKaydet/normalCalismaEkle — sadece kart_kimlik+durum+zaman
      // yazılır). Bu yüzden yalnızca `cihaz_kimlik: {$in: secili}` ile arama yapılırsa,
      // BUGÜN sadece manuel giriş/çıkışı olan (hiç cihazdan okutulmamış) bir kart aday
      // listesine HİÇ girmiyordu → kişi firmada olsa bile panelde görünmüyordu. Bu yüzden
      // eşleşme cihaz filtresi VEYA manuel kayıt olacak şekilde genişletildi; manuel
      // kayıtlar zaten belirli bir cihaza bağlı değildir, bu yüzden okuyucu seçimine
      // bakılmaksızın (kişi bazlı) sayılır.
      // Sıralama/bugün filtresi Mongo'da DEĞİL JS'te yapılır: `sunucu_zamani` alanı
      // karışık formatlı (sunucu RFC3339 "+03:00", manuel kayıtlar "Z") — string
      // karşılaştırması/sıralaması bozulduğu için grup çift/tekliği kayıyordu
      // (Melis 5 blok = Dışarıda iken panel Firmada gösteriyordu).
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: {
              vt: 'FikoYT.sistem.cihaz.olaylar',
              durum: { $in: ['koyuldu', 'cekildi'] },
              $or: [
                { cihaz_kimlik: { $in: secili } },
                { manuel: true },
              ],
          } },
          // _id (ObjectId) zamanla artar — en güvenilir zaman sıralaması
          { $sort: { _id: -1 } },
          { $limit: 500 },
          { $project: { kart_kimlik: 1, sunucu_zamani: 1 } },
        ],
      };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || (Array.isArray(istek.cevap) ? istek.cevap : []) || [];

      // Bugün filtresi YEREL tarihe göre (kart-sahibi bugunStr ile aynı mantık)
      const simdi = new Date();
      const bugunStr = `${simdi.getFullYear()}-${String(simdi.getMonth() + 1).padStart(2, '0')}-${String(simdi.getDate()).padStart(2, '0')}`;
      const bugununKartlari = new Set<string>();
      for (const o of ham) {
        const t = new Date(o.sunucu_zamani);
        if (isNaN(t.getTime())) continue;
        const s = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        if (s !== bugunStr) continue;
        const uid = String(o.kart_kimlik || '').toLowerCase();
        if (uid) bugununKartlari.add(uid);
      }
      const kartlar: any[] = [...bugununKartlari].map(uid => ({ _id: uid }));

      const kisiler: IFirmadaKisi[] = [];
      for (const k of kartlar) {
        const kartUid = k._id;
        if (!kartUid) continue;
        const durum = await this._kartDurumHesapla(kartUid);
        // Bellek içi dokunuş takibini DB'deki gerçek durumla tohumla (canlı geçişler bu temelden gitsin)
        if (durum) {
          this._dokunusVeri.set(String(kartUid).toLowerCase(), {
            firmada: !!durum.firmada,
            sonDokunusZaman: durum.sonZaman ? new Date(durum.sonZaman).getTime() : Date.now(),
          });
        }
        if (!durum?.firmada) continue;
        const kisi = await this._kisiBilgisiCek(kartUid, durum.sonCihaz, durum.sonZaman);
        if (kisi) kisiler.push(kisi);
      }
      this._listeyiUygula(kisiler);
    } catch (e) {
      console.error('[FirmadaPanel] açılış yükleme hatası', e);
    } finally {
      if (!sessiz) this.yukleniyor.set(false);
      this.cd.markForCheck();
    }
  }

  /** Hesaplanan listeyi stabil şekilde uygular:
   *  1. Sıralama sabittir (isim) — her yenilemede elemanlar yer değiştirmez.
   *  2. İçerik değişmemişse signal'a HİÇ dokunulmaz → DOM yeniden render edilmez,
   *     kapsayıcı boyutları titremez (canlı tazeleme titremesinin kaynağı buydu).
   *  3. Değişen/yeni/çıkmiş elemanlar fark edilerek uygulanır; @for track(kartUid)
   *     sayesinde değişmeyen kişilerin DOM düğümleri yeniden kurulmaz. */
  private _listeyiUygula(yeni: IFirmadaKisi[]) {
    // Sabit sıralama: isme göre (Türkçe duyarlı). Aynı isimli iki kişiyi ayırmak
    // için ikincil anahtar kart UID'si.
    const sirali = [...yeni].sort((a, b) =>
      a.isim.localeCompare(b.isim, 'tr') || String(a.kartUid).localeCompare(String(b.kartUid))
    );

    const eski = this.firmadakiler();
    if (eski.length === sirali.length) {
      const eskiMap = new Map(eski.map(k => [String(k.kartUid).toLowerCase(), k]));
      const degisti = sirali.some(n => {
        const e = eskiMap.get(String(n.kartUid).toLowerCase());
        // Tüm görünür alanlar aynıysa değişiklik sayılmaz
        return !e || e.isim !== n.isim || e.girisSaati !== n.girisSaati ||
               e.resimId !== n.resimId || e.okuyucuIsmi !== n.okuyucuIsmi;
      });
      if (!degisti) {
        this.cd.markForCheck();
        return; // hiçbir şey değişmedi → DOM'a dokunma
      }
    }

    this.firmadakiler.set(sirali);
  }

  /** Canlı bir okutma olayı geldiğinde: dokunuş gruplamayı bellek içi haritayla sürdür,
   *  geçiş anında firmadaysa listeye ekle/güncelle, değilse listeden çıkar.
   *  koyuldu+cekildi ikilisi (≤esikMs, kişiye özel, arayla gelen) TEK dokunuş sayılır — bu yüzden
   *  her ham olayda değil, yalnızca yeni bir dokunuş başladığında durum değiştirilir. */
  private async _kartiGuncelle(kartUid: string, cihazKimlik: string, olayZaman: number) {
    const anahtar = String(kartUid).toLowerCase();
    const onceki = this._dokunusVeri.get(anahtar);
    const esikMs = await this._esikGetir(kartUid);

    // Aynı dokunuşun ikinci yarısı (ör. koyuldu'nun ardından hızlı cekildi) → durum değişmez
    if (onceki && (olayZaman - onceki.sonDokunusZaman) <= esikMs) {
      onceki.sonDokunusZaman = olayZaman;
      this.cd.markForCheck();
      return;
    }

    const firmada = !(onceki?.firmada ?? false);
    this._dokunusVeri.set(anahtar, { firmada, sonDokunusZaman: olayZaman });

    if (!firmada) {
      // Listede küçük harfe normalize edilmiş ya da canlıdaki ham kasada olabilecek kimliği
      // tutarlı şekilde kaldır (case-insensitive)
      this.firmadakiler.update(liste => liste.filter(k => String(k.kartUid).toLowerCase() !== anahtar));
      this.cd.markForCheck();
      return;
    }

    const kisi = await this._kisiBilgisiCek(kartUid, cihazKimlik);
    if (!kisi) return;
    this.firmadakiler.update(liste => {
      const hedefAhtar = String(kisi.kartUid).toLowerCase();
      const temiz = liste.filter(k => String(k.kartUid).toLowerCase() !== hedefAhtar);
      // Sabit sıralama (isim) — canlı eklemelerde liste düzeni yer değiştirmez
      return [...temiz, kisi].sort((a, b) =>
        a.isim.localeCompare(b.isim, 'tr') || String(a.kartUid).localeCompare(String(b.kartUid))
      );
    });
    this.cd.markForCheck();
  }

  /** Kartın BUGÜNKÜ okutma dokunuşlarını (koyuldu+cekildi, zaman sıralı) gruplayıp
   *  "giriş/çıkış" dengesini hesaplar — kart-sahibi.firmadaMi ile BİREBİR aynı mantık.
   *
   *  KURAL (kart-sahibi.firmadaMi ile aynı):
   *    - Bugün hiç hareket YOKSA -> Dışarıda (return firmada:false).
   *    - Bugünkü dokunuş grubu TEK sayıdaysa -> Firmada; ÇİFT ise -> Dışarıda.
   *
   *  GÜN SINIRI ÖNEMLİ: Melis bugün hiç okumadıysa (dün girişi olsa bile), kart-sahibi
   *  bugüne bakıp Dışarıda der. Eğer tüm geçmiş sayılırsa dünkü tek giriş "tek dokunuş =
   *  içeride" sanılıp yanlışlıkla Firmada gösterilir. Bu yüzden yalnızca bugünün olayları
   *  değerlendirilir.
   *
   *  Canlı geçişler _dokunusVeri (bellek içi) üzerinden yapıldığı için bu fonksiyon
   *  yalnızca AÇILIŞ tohumlamada kullanılır. */
  private async _kartDurumHesapla(kartUid: string): Promise<{ firmada: boolean; sonZaman: string; sonCihaz: string } | null> {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.pdks_kart_hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: {
              vt: 'FikoYT.sistem.cihaz.olaylar',
              durum: { $in: ['koyuldu', 'cekildi'] },
              // kart UID'si cihazdan farklı harf kasasıyla gelebildiği için
              // case-insensitive tam eşleşme ($expr + $toLower)
              $expr: { $eq: [
                { $toLower: { $ifNull: ['$kart_kimlik', ''] } },
                String(kartUid || '').toLowerCase(),
              ] },
          } },
          // _id (ObjectId) zamanla artar — `sunucu_zamani` string sıralaması karışık
          // formatlarda (+03:00 / Z) bozulduğu için sıralamayı Mongo'ya DEĞİL JS'e bırakıyoruz
          { $sort: { _id: -1 } },
          { $limit: 300 },
          { $project: { sunucu_zamani: 1, cihaz_kimlik: 1 } },
        ],
      };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || (Array.isArray(istek.cevap) ? istek.cevap : []) || [];
      if (!ham.length) {
        // Bu kart için HİÇ hareket yok → kart-sahibi'nin firmadaMi kuralıyla "Dışarıda".
        return { firmada: false, sonZaman: new Date().toISOString(), sonCihaz: '' };
      }

      // Bugün filtresi YEREL tarihe göre (kart-sahibi.firmadaMi → bugunStr ile aynı)
      const simdi = new Date();
      const bugunStr = `${simdi.getFullYear()}-${String(simdi.getMonth() + 1).padStart(2, '0')}-${String(simdi.getDate()).padStart(2, '0')}`;
      const olaylar = ham
        .map(o => ({ zaman: new Date(o.sunucu_zamani).getTime(), cihaz: o.cihaz_kimlik || '' }))
        .filter(o => !isNaN(o.zaman))
        .filter(o => {
          const t = new Date(o.zaman);
          const s = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
          return s === bugunStr;
        })
        .sort((a, b) => a.zaman - b.zaman); // JS'te sayısal sıralama — format karışıklığına dayanıklı
      if (!olaylar.length) {
        // Bugün hiç hareket yok → Dışarıda (firmadaMi: bugunGun yoksa false)
        return { firmada: false, sonZaman: new Date().toISOString(), sonCihaz: '' };
      }

      // Dokunuş gruplama: ≤esikMs (kişiye özel, DB'den) arayla gelen olaylar tek dokunuş
      // (koyuldu+cekildi ikilisi). Grup sayısı TEK → son segment açık → Firmada;
      // ÇİFT → son segment kapalı → Dışarıda (kart-sahibi segment mantığıyla aynı).
      const esikMs = await this._esikGetir(kartUid);
      const gruplar: { ilkZaman: number; sonZaman: number; cihaz: string }[] = [];
      let grupBas = olaylar[0];
      let grupSon = olaylar[0];
      for (let i = 1; i < olaylar.length; i++) {
        if (olaylar[i].zaman - grupSon.zaman <= esikMs) {
          grupSon = olaylar[i];
        } else {
          gruplar.push({ ilkZaman: grupBas.zaman, sonZaman: grupSon.zaman, cihaz: grupBas.cihaz });
          grupBas = olaylar[i];
          grupSon = olaylar[i];
        }
      }
      gruplar.push({ ilkZaman: grupBas.zaman, sonZaman: grupSon.zaman, cihaz: grupBas.cihaz });

      const sonGrup = gruplar[gruplar.length - 1];
      return {
        firmada: gruplar.length % 2 === 1,
        sonZaman: new Date(sonGrup.sonZaman).toISOString(),
        sonCihaz: sonGrup.cihaz || '',
      };
    } catch {
      return null;
    }
  }

  /** Kart-sahibi ayarındaki kişiye özel dokunuş gruplama eşiğini (ms) DB'den okur.
   *  kart-sahibi bileşenindeki `grupEsikSaniye` ile BİREBİR AYNI DB alanını okur
   *  (ek.grupEsikSaniye) — böylece iki taraf HER ZAMAN aynı eşiği kullanır ve firmada-panel
   *  kart-sahibi'ndeki tolerans/gruplama ayarını görmezden gelmez. Sonuç, kart başına
   *  önbelleğe alınır (bkz. _esikCache) ve kart-sahibi tarafında ayar değiştiğinde
   *  `hareketDegisti$` sinyaliyle temizlenir. Alan tanımlı değilse (eski kayıt / hiç
   *  ayarlanmamış) DOKUNUS_ESIK_VARSAYILAN_MS kullanılır. */
  private async _esikGetir(kartUid: string): Promise<number> {
    const anahtar = String(kartUid || '').toLowerCase();
    const onbellek = this._esikCache.get(anahtar);
    if (onbellek !== undefined) return onbellek;

    let esikMs = DOKUNUS_ESIK_VARSAYILAN_MS;
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: {
              'ek.bilesen': 'hmi-kart-sahibi',
              $expr: { $eq: [
                { $toLower: { $ifNull: ['$ek.kartUid', ''] } },
                anahtar,
              ] },
          } },
          { $sort: { _id: -1 } },
          { $limit: 1 },
          { $project: { 'ek.grupEsikSaniye': 1 } },
        ],
      };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || (Array.isArray(istek.cevap) ? istek.cevap : []) || [];
      const saniye = Number(ham[0]?.ek?.grupEsikSaniye);
      if (!isNaN(saniye) && saniye >= 0) esikMs = saniye * 1000;
    } catch {
      // sorgu başarısız — varsayılan eşikle devam
    }
    this._esikCache.set(anahtar, esikMs);
    return esikMs;
  }

  /** DB'den kart-sahibi bileşen kaydını çekip IFirmadaKisi üretir. */
  private async _kisiBilgisiCek(kartUid: string, cihazKimlik: string, girisZamaniIso?: string): Promise<IFirmadaKisi | null> {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: {
              // Kişi kayıtları `hareketler` içindeki İÇERİK belgeleridir; bileşen kimliği
              // belgenin `tur` alanında DEĞİL, içeriğin `ek.bilesen` alanında tutulur
              // (ikart-kutusu: `ic?.ek?.bilesen`). Bu yüzden `tur:'hmi-kart-sahibi'` ile
              // yapılan eski eşleşme HİÇBİR kaydı bulamıyordu → panel boş kalıyordu.
              'ek.bilesen': 'hmi-kart-sahibi',
              // Okuyucudan gelen `kart_kimlik` ile kaydedilen `ek.kartUid` arasında
              // büyük/küçük harf farkı oluşabiliyor; bu yüzden $expr + $toLower ile
              // case-insensitive tam eşleşme yapılır (kart-sahibi canlı eşleştirmede
              // aynı nedenle toUpperCase kullanıyor).
              $expr: { $eq: [
                { $toLower: { $ifNull: ['$ek.kartUid', ''] } },
                String(kartUid || '').toLowerCase(),
              ] },
          } },
          // Birden fazla kayıt olursa en güncelini al
          { $sort: { _id: -1 } },
          { $limit: 1 },
          { $project: { 'ek.sahipIsmi': 1, 'ek.sahipResim': 1, 'ek.kartUid': 1 } },
        ],
      };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || (Array.isArray(istek.cevap) ? istek.cevap : []) || [];
      const kayit = ham[0];
      if (!kayit) return null;

      const resimId  = kayit.ek?.sahipResim || '';
      const resimUrl = this.avatar.profilResimUrl(resimId, '128x128');
      const cihaz    = this.wsService.cihazlar$.getValue?.()?.find?.((c: any) => c.cihaz_kimlik === cihazKimlik);

      return {
        // UID küçük harfe normalize: @for track anahtarı canlı/DB-yenileme arasında
        // aynı kalır → DOM düğümleri gereksiz yeniden kurulmaz, liste titremez
        kartUid: String(kartUid || '').toLowerCase(),
        isim: kayit.ek?.sahipIsmi || 'İsimsiz',
        resimId,
        resimUrl,
        okuyucuKimlik: cihazKimlik,
        okuyucuIsmi:   cihaz?.cihaz_ismi || cihazKimlik || 'Bilinmiyor',
        girisSaati:    girisZamaniIso ? this._saatFormatla(girisZamaniIso) : this._simdi(),
      };
    } catch {
      return null;
    }
  }

  private _simdi(): string {
    return this._saatFormatla(new Date());
  }

  private _saatFormatla(zaman: string | Date): string {
    const d = typeof zaman === 'string' ? new Date(zaman) : zaman;
    if (isNaN(d.getTime())) return this._simdi();
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  }
}
